import React from 'react'

import { API_BASE_URL } from '../../config/api'
import { clickableProps } from '../../utils/clickableProps'

const MediaGrid = ({ media, onMediaClick }) => {
  if (!media || media.length === 0) {
    return <EmptyMediaState />
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {media.map((mediaItem) => (
        <MediaItem
          key={mediaItem._id}
          mediaItem={mediaItem}
          onClick={() => onMediaClick(mediaItem)}
        />
      ))}
    </div>
  )
}

const MediaItem = React.memo(({ mediaItem, onClick }) => {
  const getMediaUrl = (mediaItem) => {
    return `${API_BASE_URL}${mediaItem.path}`
  }

  const isVideo = (mimetype) => {
    return mimetype.startsWith('video/')
  }

  return (
    <div
      className="group relative aspect-square bg-gray-800 rounded-xl overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
      {...clickableProps(
        onClick,
        `Open ${mediaItem.customName || mediaItem.originalName || 'media item'}`
      )}
    >
      {isVideo(mediaItem.mimetype) ? (
        <VideoThumbnail mediaItem={mediaItem} getMediaUrl={getMediaUrl} />
      ) : (
        <ImageThumbnail mediaItem={mediaItem} getMediaUrl={getMediaUrl} />
      )}

      <MediaOverlay mediaItem={mediaItem} />
    </div>
  )
})

const VideoThumbnail = ({ mediaItem, getMediaUrl }) => (
  <div className="relative w-full h-full">
    <video
      src={getMediaUrl(mediaItem)}
      className="w-full h-full object-cover"
      muted
      preload="none"
    />
    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
        <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
    </div>
  </div>
)

const ImageThumbnail = ({ mediaItem, getMediaUrl }) => (
  <img
    src={getMediaUrl(mediaItem)}
    alt={mediaItem.customName || mediaItem.originalName}
    className="w-full h-full object-cover"
    loading="lazy"
    decoding="async"
  />
)

const MediaOverlay = ({ mediaItem }) => (
  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300">
    <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      <p className="text-white text-xs font-medium truncate">
        {mediaItem.customName || mediaItem.originalName}
      </p>
      {mediaItem.description && (
        <p className="text-gray-300 text-xs truncate">
          {mediaItem.description}
        </p>
      )}
    </div>
  </div>
)

const EmptyMediaState = () => (
  <div className="text-center py-8">
    <div className="w-16 h-16 mx-auto mb-4 bg-white/10 rounded-full flex items-center justify-center">
      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
    <p className="text-gray-300">No media uploaded yet</p>
    <p className="text-gray-400 text-sm mt-1">Upload photos and videos to see them here</p>
  </div>
)

export default MediaGrid